import { TableRow as MuiTableRow, useTheme } from '@mui/material';
import { TableColumn } from '../Table.props';
import { TableRowCell } from './TableRowCell';

export interface TableDataRowProps<T> {
  row: T;
  index: number;
  columns: TableColumn<T>[];
  isSelected?: boolean;
  onRowClick?: (row: T, index: number) => void;
  hover?: boolean;
  striped?: boolean;
  columnStickyPositions?: Map<string | keyof T, number>;
}

/**
 * TableDataRow - Renders a single table row with all its cells
 *
 * Features:
 * - Handles row selection state
 * - Manages row click events
 * - Applies hover and striped styling
 * - Renders all column cells including selection and actions
 * - Proper event handling and styling
 */
export const TableDataRow = <T,>({
  row,
  index,
  columns,
  isSelected,
  onRowClick,
  hover,
  striped,
  columnStickyPositions,
}: TableDataRowProps<T>) => {
  const theme = useTheme();

  return (
    <MuiTableRow
      hover={hover}
      selected={isSelected}
      onClick={onRowClick ? () => onRowClick(row, index) : undefined}
      sx={{
        cursor: onRowClick ? 'pointer' : 'default',
        ...(striped &&
          index % 2 === 1 && {
            backgroundColor: theme.palette.State.striped, // Solid color #E3E3E4
          }),
        ...(isSelected && {
          backgroundColor: theme.palette.State.selected, // Override MUI's default to match sticky cells #FCFAFF
        }),
      }}
    >
      {columns.map((column) => (
        <TableRowCell
          key={String(column.id)}
          column={column}
          row={row}
          index={index}
          stickyPosition={columnStickyPositions?.get(column.id)}
          isStriped={striped && index % 2 === 1}
          isSelected={isSelected}
        />
      ))}
    </MuiTableRow>
  );
};
